import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import QuoteSummary from '../components/QuoteSummary';
import QuoteLineItemsEditor from '../components/QuoteLineItemsEditor';
import {
  getDirectJobById,
  withdrawDirectJob,
  acceptDirectJob,
  declineDirectJob,
} from '../api/directJob';
import { saveQuoteDraft } from '../api/quote';
import { DirectJob } from '../types/directJob';
import { TaskInput, MaterialInput } from '../types/quote';
import { useAuth } from '../context/AuthContext';

const DirectJobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [directJob, setDirectJob] = useState<DirectJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  const [editingQuote, setEditingQuote] = useState(false);
  const [tasks, setTasks] = useState<TaskInput[]>([]);
  const [materials, setMaterials] = useState<MaterialInput[]>([]);
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [vatRate, setVatRate] = useState(15);

  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [reopenToMarket, setReopenToMarket] = useState(false);

  const load = () => {
    if (!id) return;
    getDirectJobById(parseInt(id))
      .then((dj) => {
        setDirectJob(dj);
        if (dj.quote) {
          setTasks(dj.quote.tasks.map((t) => ({
            description: t.description,
            resources: t.resources.map((r) => ({
              description: r.description,
              ratePerHour: r.ratePerHour,
              hours: r.hours,
              resourceCatalogueItemId: r.resourceCatalogueItemId,
            })),
          })));
          setMaterials(dj.quote.materials.map((m) => ({ description: m.description, quantity: m.quantity, pricePerUnit: m.pricePerUnit })));
          setIsVatRegistered(dj.quote.isVatRegistered);
          setVatRate(dj.quote.vatRate);
        }
      })
      .catch(() => setError('Failed to load this Direct Job.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (error || !directJob) {
    return (
      <div className="min-h-screen bg-surface-container-lowest">
        <TopAppBar />
        <main className="container mx-auto px-4 py-8 max-w-2xl text-center text-error">{error ?? 'Not found.'}</main>
        <BottomNavBar />
      </div>
    );
  }

  const isContractorView = !!currentUser?.isContractor;

  const handleWithdraw = async () => {
    setActionInProgress(true);
    try {
      await withdrawDirectJob(directJob.id);
      load();
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.message ?? 'Failed to withdraw.');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSaveQuote = async () => {
    if (!directJob.quote) return;
    setActionInProgress(true);
    try {
      await saveQuoteDraft(directJob.quote.id, {
        isVatRegistered,
        vatRate,
        tasks: tasks.filter((t) => t.description.trim() !== '').map((t) => ({ ...t, resources: t.resources.filter((r) => r.description.trim() !== '') })),
        materials: materials.filter((m) => m.description.trim() !== ''),
      });
      setEditingQuote(false);
      load();
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.message ?? 'Failed to save Quote.');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleAccept = async () => {
    setActionInProgress(true);
    try {
      const updated = await acceptDirectJob(directJob.id);
      if (updated.convertedJobId) navigate(`/jobs/${updated.convertedJobId}`);
      else load();
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.message ?? 'Failed to accept.');
      setActionInProgress(false);
    }
  };

  const handleDecline = async () => {
    setActionInProgress(true);
    try {
      const updated = await declineDirectJob(directJob.id, declineReason || undefined, reopenToMarket);
      if (reopenToMarket && updated.convertedJobId) navigate(`/jobs/${updated.convertedJobId}`);
      else load();
    } catch (e: any) {
      setError(e.response?.data?.message ?? e.message ?? 'Failed to decline.');
    } finally {
      setActionInProgress(false);
      setDeclining(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-container-lowest font-sans antialiased">
      <TopAppBar />
      <main className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-on-surface">{directJob.title}</h1>
          <p className="text-sm text-on-surface-variant mt-1">{directJob.location} · {directJob.categoryNames.join(', ')}</p>
        </div>

        {error && <p className="text-error text-sm">{error}</p>}

        <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-6">
          <p className="text-on-surface whitespace-pre-wrap">{directJob.description}</p>
        </div>

        {isContractorView && (
          <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-6 space-y-2">
            <p className="text-sm text-on-surface-variant">Sent to: {directJob.recipientEmail}</p>
            <p className="text-sm text-on-surface-variant">Status: <span className="font-semibold text-on-surface">{directJob.status}</span></p>
            {directJob.status === 'Rejected' && directJob.rejectionReason && (
              <p className="text-sm text-error">Rejected: {directJob.rejectionReason}</p>
            )}
            {(directJob.status === 'UnderReview' || directJob.status === 'PendingClient') && (
              <button
                onClick={handleWithdraw}
                disabled={actionInProgress}
                className="text-error text-sm hover:underline disabled:opacity-50"
              >
                Withdraw this Direct Job
              </button>
            )}
          </div>
        )}

        {directJob.quote && !editingQuote && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-title-md text-on-surface">Quote</h2>
              {isContractorView && directJob.status === 'UnderReview' && (
                <button onClick={() => setEditingQuote(true)} className="text-sm text-primary hover:underline">Edit</button>
              )}
            </div>
            <QuoteSummary quote={directJob.quote} />
          </div>
        )}

        {editingQuote && (
          <div className="bg-surface-container-low border border-outline-variant rounded-2xl p-4 space-y-4">
            <QuoteLineItemsEditor
              tasks={tasks}
              materials={materials}
              isVatRegistered={isVatRegistered}
              vatRate={vatRate}
              onTasksChange={setTasks}
              onMaterialsChange={setMaterials}
              onVatChange={(v, r) => { setIsVatRegistered(v); setVatRate(r); }}
            />
            <div className="flex gap-3">
              <button onClick={handleSaveQuote} disabled={actionInProgress} className="px-4 py-1.5 rounded-lg text-sm bg-primary text-on-primary disabled:opacity-50">
                {actionInProgress ? 'Saving...' : 'Save changes'}
              </button>
              <button onClick={() => setEditingQuote(false)} disabled={actionInProgress} className="text-sm text-outline hover:underline">Cancel</button>
            </div>
          </div>
        )}

        {!isContractorView && directJob.status === 'PendingClient' && (
          <div className="bg-surface rounded-2xl shadow-sm border border-outline-variant p-6 space-y-4">
            <p className="text-sm text-on-surface-variant">
              {directJob.contractorCompanyName} sent you this Quote directly. Accept to get started, or decline it.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleAccept}
                disabled={actionInProgress}
                className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary disabled:opacity-50"
              >
                Accept
              </button>
              <button
                onClick={() => setDeclining(true)}
                disabled={actionInProgress}
                className="px-5 py-2 rounded-lg text-sm font-medium border border-outline-variant text-on-surface disabled:opacity-50"
              >
                Decline
              </button>
            </div>

            {declining && (
              <div className="pt-4 border-t border-outline-variant space-y-3">
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Reason (optional)"
                  rows={3}
                  className="w-full p-2 rounded-lg border border-outline-variant bg-surface text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <label className="flex items-center gap-2 text-sm text-on-surface-variant">
                  <input type="checkbox" checked={reopenToMarket} onChange={(e) => setReopenToMarket(e.target.checked)} />
                  Keep this job open for other contractors to apply
                </label>
                <div className="flex gap-3">
                  <button onClick={handleDecline} disabled={actionInProgress} className="px-4 py-1.5 rounded-lg text-sm bg-error text-on-error disabled:opacity-50">
                    {actionInProgress ? 'Submitting...' : 'Confirm decline'}
                  </button>
                  <button onClick={() => setDeclining(false)} disabled={actionInProgress} className="text-sm text-outline hover:underline">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      <BottomNavBar />
    </div>
  );
};

export default DirectJobDetailPage;
